import React, { useState, useEffect } from 'react';
import AdminPageTitle from '../components/AdminPageTitle';
import {
  fetchCurrencyRates,
  createCurrencyRate,
  updateCurrencyRate,
  deleteCurrencyRate,
} from '../services/currency';
import './Currency.css';

const Currency = () => {
  const [rates, setRates] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const itemsPerPage = 10;

  const [formData, setFormData] = useState({
    base_currency: 'Naira',
    base_currency_symbol: '₦',
    base_currency_code: 'NGN',
    base_currency_rate: '220',
    target_currency: 'Chinese Yuan',
    target_currency_symbol: '¥',
    target_currency_code: 'CNY',
    target_currency_rate: '1',
  });

  useEffect(() => {
    loadCurrencyRates();
  }, [currentPage]);

  const loadCurrencyRates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchCurrencyRates({
        page: currentPage,
        per_page: itemsPerPage,
      });
      setRates(response.data || []);
      setTotalPages(response.last_page || 1);
    } catch (err) {
      setError('Failed to load currency rates: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const submitData = {
        ...formData,
        base_currency_rate: parseFloat(formData.base_currency_rate),
        target_currency_rate: parseFloat(formData.target_currency_rate),
      };

      if (editingRate) {
        await updateCurrencyRate(editingRate.id, submitData);
        setSuccess('Currency rate updated successfully!');
      } else {
        await createCurrencyRate(submitData);
        setSuccess('Currency rate created successfully!');
      }

      setShowModal(false);
      resetForm();
      loadCurrencyRates();
    } catch (err) {
      setError('Failed to save currency rate: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleEdit = (rate) => {
    setEditingRate(rate);
    setFormData({
      base_currency: rate.base_currency || 'Naira',
      base_currency_symbol: rate.base_currency_symbol || '₦',
      base_currency_code: rate.base_currency_code || 'NGN',
      base_currency_rate: rate.base_currency_rate?.toString() || '220',
      target_currency: rate.target_currency || 'Chinese Yuan',
      target_currency_symbol: rate.target_currency_symbol || '¥',
      target_currency_code: rate.target_currency_code || 'CNY',
      target_currency_rate: rate.target_currency_rate?.toString() || '1',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this currency rate?')) {
      try {
        await deleteCurrencyRate(id);
        setSuccess('Currency rate deleted successfully!');
        loadCurrencyRates();
      } catch (err) {
        setError('Failed to delete currency rate: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  const resetForm = () => {
    setEditingRate(null);
    setFormData({
      base_currency: 'Naira',
      base_currency_symbol: '₦',
      base_currency_code: 'NGN',
      base_currency_rate: '220',
      target_currency: 'Chinese Yuan',
      target_currency_symbol: '¥',
      target_currency_code: 'CNY',
      target_currency_rate: '1',
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
    setError(null);
    setSuccess(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="currency-management">
      <AdminPageTitle title="Currency" />
      <div className="currency-header">
        <h1>Currency Rate Management</h1>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          Add New Currency Rate
        </button>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#fee', color: '#c33', borderRadius: '4px' }}>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success" style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#efe', color: '#3c3', borderRadius: '4px' }}>
          {success}
        </div>
      )}

      {loading && <p>Loading currency rates...</p>}

      {!loading && rates.length === 0 && !error && (
        <p>No currency rates found. Add a new one!</p>
      )}

      {!loading && rates.length > 0 && (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Base Currency</th>
                  <th>Base Rate</th>
                  <th>Target Currency</th>
                  <th>Target Rate</th>
                  <th>Exchange Rate</th>
                  <th>Created By</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rates.map((rate) => {
                  const exchangeRate = rate.base_currency_rate && rate.target_currency_rate
                    ? (parseFloat(rate.base_currency_rate) / parseFloat(rate.target_currency_rate)).toFixed(6)
                    : 'N/A';
                  
                  return (
                    <tr key={rate.id}>
                      <td>
                        <div className="currency-cell">
                          <span className="currency-symbol">{rate.base_currency_symbol}</span>
                          <div>
                            <div className="currency-name">{rate.base_currency}</div>
                            <div className="currency-code">{rate.base_currency_code}</div>
                          </div>
                        </div>
                      </td>
                      <td>{parseFloat(rate.base_currency_rate).toLocaleString()}</td>
                      <td>
                        <div className="currency-cell">
                          <span className="currency-symbol">{rate.target_currency_symbol}</span>
                          <div>
                            <div className="currency-name">{rate.target_currency}</div>
                            <div className="currency-code">{rate.target_currency_code}</div>
                          </div>
                        </div>
                      </td>
                      <td>{parseFloat(rate.target_currency_rate).toLocaleString()}</td>
                      <td>
                        <span className="exchange-rate">
                          {rate.base_currency_symbol}1 = {rate.target_currency_symbol}{exchangeRate}
                        </span>
                      </td>
                      <td>{rate.creator?.name || 'N/A'}</td>
                      <td>{formatDate(rate.created_at)}</td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn-edit" onClick={() => handleEdit(rate)}>
                            Edit
                          </button>
                          <button className="btn-delete" onClick={() => handleDelete(rate.id)}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              <div className="pagination-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingRate ? 'Edit Currency Rate' : 'Add New Currency Rate'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="currency-form">
              <div className="form-section">
                <h3>Base Currency (Naira)</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="base_currency">Currency Name</label>
                    <input
                      type="text"
                      id="base_currency"
                      name="base_currency"
                      value={formData.base_currency}
                      onChange={handleInputChange}
                      required
                      placeholder="Naira"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="base_currency_symbol">Symbol</label>
                    <input
                      type="text"
                      id="base_currency_symbol"
                      name="base_currency_symbol"
                      value={formData.base_currency_symbol}
                      onChange={handleInputChange}
                      required
                      placeholder="₦"
                      maxLength="8"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="base_currency_code">Code</label>
                    <input
                      type="text"
                      id="base_currency_code"
                      name="base_currency_code"
                      value={formData.base_currency_code}
                      onChange={handleInputChange}
                      required
                      placeholder="NGN"
                      maxLength="8"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="base_currency_rate">Rate</label>
                    <input
                      type="number"
                      id="base_currency_rate"
                      name="base_currency_rate"
                      value={formData.base_currency_rate}
                      onChange={handleInputChange}
                      required
                      step="0.000001"
                      min="0"
                      placeholder="220"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Target Currency (Chinese Yuan)</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="target_currency">Currency Name</label>
                    <input
                      type="text"
                      id="target_currency"
                      name="target_currency"
                      value={formData.target_currency}
                      onChange={handleInputChange}
                      required
                      placeholder="Chinese Yuan"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="target_currency_symbol">Symbol</label>
                    <input
                      type="text"
                      id="target_currency_symbol"
                      name="target_currency_symbol"
                      value={formData.target_currency_symbol}
                      onChange={handleInputChange}
                      required
                      placeholder="¥"
                      maxLength="8"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="target_currency_code">Code</label>
                    <input
                      type="text"
                      id="target_currency_code"
                      name="target_currency_code"
                      value={formData.target_currency_code}
                      onChange={handleInputChange}
                      required
                      placeholder="CNY"
                      maxLength="8"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="target_currency_rate">Rate</label>
                    <input
                      type="number"
                      id="target_currency_rate"
                      name="target_currency_rate"
                      value={formData.target_currency_rate}
                      onChange={handleInputChange}
                      required
                      step="0.000001"
                      min="0"
                      placeholder="1"
                    />
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingRate ? 'Update Rate' : 'Create Rate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Currency;
